/* ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════════
 * W137 cut 2 (R6, R7) — MOVING A WORKOUT BETWEEN DAYS, AND THE ORDER OF TWO ON ONE DAY. The arithmetic, pure, so
 * `dayDrag.test.ts` executes it; `components/DayDrag.tsx` is the gesture (the builder's dnd-kit contract) and the page
 * decides what a drop MEANS (ClientTraining writes `coach_assignments`; the Program board, cut 3, `coach_program_days`).
 *
 * His words (2026-10-02): *"we need a drag icon like this so the 4 arrow thing. so we can drag and place somewhere. if
 * dragged to a day which has a workout then ok theres 2 workouts in that day assigned. the top one needs to be done first."*
 * ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════════ */

/** Days in order, each with its draggable ids top to bottom. A plain record so a render can read it by day. */
export type DayArrangement = Readonly<Record<string, readonly string[]>>;

/** The order of a day's items: `day_position`, then when it was assigned, then the id — so two rows that were never
 *  ordered keep the order they always had (R7: readers sort by (scheduled_date, day_position, assigned_at)). */
export function byDayOrder(a: { dayPosition: number; assignedAtISO: string | null; id: string },
                           b: { dayPosition: number; assignedAtISO: string | null; id: string }): number {
  if (a.dayPosition !== b.dayPosition) return a.dayPosition - b.dayPosition;
  const ta = a.assignedAtISO ?? "", tb = b.assignedAtISO ?? "";
  if (ta !== tb) return ta < tb ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/** Which day holds `id`, and where. */
export function findItem(arr: DayArrangement, id: string): { dayKey: string; index: number } | null {
  for (const [dayKey, ids] of Object.entries(arr)) {
    const index = ids.indexOf(id);
    if (index !== -1) return { dayKey, index };
  }
  return null;
}

/** `id` taken out of wherever it is and put at `index` of `dayKey` (clamped). A day not in the arrangement changes
 *  nothing — a drop outside the drawn range is no drop. */
export function moveItem(arr: DayArrangement, id: string, dayKey: string, index: number): DayArrangement {
  if (!(dayKey in arr)) return arr;
  const out: Record<string, string[]> = {};
  for (const [k, ids] of Object.entries(arr)) out[k] = ids.filter((x) => x !== id);
  const target = out[dayKey]!;
  target.splice(Math.max(0, Math.min(Math.trunc(index), target.length)), 0, id);
  return out;
}

/** The writes a drop needs. `moved` is set when the item changed day; `positions` are every row whose stored order is
 *  no longer its index on its (new or old) day — only the rows that change, so a plain move writes one or two. */
export type DropPlan = {
  id: string;
  moved: { from: string; to: string } | null;
  positions: { id: string; position: number }[];
};

export function dropPlan(before: DayArrangement, after: DayArrangement, id: string,
                         stored: Readonly<Record<string, number>>): DropPlan | null {
  const from = findItem(before, id), to = findItem(after, id);
  if (from === null || to === null) return null;
  if (from.dayKey === to.dayKey && from.index === to.index) return null;
  const positions: { id: string; position: number }[] = [];
  const days = from.dayKey === to.dayKey ? [to.dayKey] : [to.dayKey, from.dayKey];
  for (const day of days) {
    (after[day] ?? []).forEach((x, i) => {
      // only a row whose stored order is not its index now — a move to an empty day at 0 writes the day alone
      if ((stored[x] ?? 0) !== i) {
        positions.push({ id: x, position: i });
      }
    });
  }
  return { id, moved: from.dayKey === to.dayKey ? null : { from: from.dayKey, to: to.dayKey }, positions };
}

/** Where a pointer at `y` lands among a day's items, given their drawn midpoints (the carried one left out). */
export function indexAt(mids: readonly number[], y: number): number {
  let n = 0;
  for (const m of mids) if (m < y) n += 1;
  return n;
}

/** The at-rest icons' names — the workout and its day, so four weeks of them can be told apart (R6b's wording). */
export function moveHandleLabel(name: string, day: string): string {
  return `Move ${name} — ${day}`;
}
export function copyLabel(name: string, day: string): string {
  return `Copy ${name} — ${day}`;
}

/** Week editor stage 2 — a workout assigned from the library panel lands at `index` among its day's workouts (Ioannis 10-19:
 *  "the order … [is] as intended from the coach"). `order` is the day's assignment ids as drawn (the new one absent); the
 *  writes are every row whose position is not its new index — the new row always (its stored position is the assign's). */
export function placeOnDay(order: readonly string[], newId: string, index: number,
                           stored: Readonly<Record<string, number>>): { id: string; position: number }[] {
  const at = Math.max(0, Math.min(index, order.length));
  const next = [...order.slice(0, at), newId, ...order.slice(at)];
  return next.map((id, position) => ({ id, position })).filter((p) => p.id === newId || stored[p.id] !== p.position);
}
